import { Alert, Box, Button, Group, ScrollArea, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState, type ReactNode } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { errorMessage } from '../../utils/errors.js';
import { useFieldAdoption } from '../Adopt/useFieldAdoption.js';
import { SectionHeading } from '../SectionHeading/SectionHeading.js';
import { EnvironmentChecklist, FieldFilter, RecordNav } from './PushReviewNav.js';
import type { PushOpRow } from './PushOps.js';
import { PushRecords } from './PushRecords.js';
import { recordName } from './pushRecordsView.js';
import { adoptTargetOf, reviewTitleParts, viewedEnvironment } from './pushReviewView.js';
import type { PushPreview, PushRequest } from './pushSummary.js';
import { SentenceText } from '../SentenceText/SentenceText.js';
import { ReviewEmpty } from './ReviewEmpty.js';
import type { PushWorkflow } from './usePushWorkflow.js';
import { useRecordsView } from './useRecordsView.js';

export const PushReview = (props: { request: PushRequest; workflow: PushWorkflow; onCancel: () => void }) => {
    const isMobile = useIsMobile();
    const [pickedEnvironment, setPickedEnvironment] = useState<string | null>(null);
    const { workflow } = props;

    const tickedNames = workflow.names.filter((name) => workflow.ticked.has(name));
    const environment = viewedEnvironment(workflow.names, workflow.ticked, pickedEnvironment);
    const title = reviewTitleParts(props.request.cells, tickedNames);
    const fieldAdoption = useFieldAdoption();

    const checklist = (
        <EnvironmentChecklist
            names={workflow.names}
            previews={workflow.previews}
            previewPending={workflow.previewPending}
            ticked={workflow.ticked}
            onToggle={workflow.onToggle}
            advisory={workflow.advisory}
        />
    );

    return (
        <>
            {fieldAdoption.dialog}
            <Group
                px={isMobile ? 'md' : 'lg'}
                py="md"
                style={{ borderBottom: '1px solid var(--rgs-line)' }}
                justify="space-between"
                wrap="nowrap"
                align="flex-start"
            >
                <Group gap="sm" wrap="wrap" align="center" style={{ minWidth: 0 }}>
                    <SentenceText parts={title} noWrapMono fz="h2" ff="heading" style={{ overflowWrap: 'anywhere' }} />
                    <EnvironmentPicker
                        tickedNames={tickedNames}
                        environment={environment}
                        onPick={setPickedEnvironment}
                    />
                </Group>
                <Button
                    variant="subtle"
                    color="gray"
                    size="compact-sm"
                    onClick={props.onCancel}
                    style={{ flexShrink: 0 }}
                >
                    Close
                </Button>
            </Group>
            <Box
                style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '260px 1fr' }}
            >
                <PushReviewEnvironment
                    key={environment ?? ''}
                    environment={environment}
                    preview={environment ? workflow.previews.get(environment) : undefined}
                    previewPending={workflow.previewPending}
                    previewError={workflow.previewError}
                    isMobile={isMobile}
                    checklist={checklist}
                    fieldAdoption={fieldAdoption}
                />
            </Box>
            <Stack gap="xs" px={isMobile ? 'md' : 'lg'} py="md" style={{ borderTop: '1px solid var(--rgs-line)' }}>
                {workflow.risky.length > 0 && (
                    <Alert color="red" p="xs">
                        Production: {workflow.risky.join(', ')}
                    </Alert>
                )}
                {workflow.runError ? (
                    <Alert color="red" p="xs">
                        {errorMessage(workflow.runError)}
                    </Alert>
                ) : null}
                <Group justify="space-between" wrap="wrap" gap="xs">
                    <TextInput
                        size="sm"
                        placeholder="Note (optional)"
                        value={workflow.note}
                        onChange={(e) => workflow.onNote(e.currentTarget.value)}
                        style={{ flex: 1, minWidth: 200 }}
                    />
                    <Group gap="xs">
                        <Button variant="default" onClick={props.onCancel}>
                            Cancel
                        </Button>
                        <Button
                            variant="filled"
                            disabled={!workflow.live}
                            loading={workflow.running}
                            onClick={workflow.onConfirm}
                            title={workflow.canApply ? undefined : 'Sign in to push'}
                        >
                            {workflow.confirmLabel}
                        </Button>
                    </Group>
                </Group>
            </Stack>
        </>
    );
};

/**
 * The nav and the pane, over one environment's dry run — keyed on the environment in `PushReview`
 * so switching which one is on screen remounts `useRecordsView` with nothing searched or picked,
 * rather than a `useEffect` resetting it after the fact.
 */
const PushReviewEnvironment = ({
    environment,
    preview,
    previewPending,
    previewError,
    isMobile,
    checklist,
    fieldAdoption,
}: {
    environment: string | null;
    preview: PushPreview | undefined;
    previewPending: boolean;
    previewError: unknown;
    isMobile: boolean;
    checklist: ReactNode;
    fieldAdoption: ReturnType<typeof useFieldAdoption>;
}) => {
    const view = useRecordsView(preview?.ops);

    /**
     * The drawer's per-field **Adopt ▾**, on an update the push would make: its "before" is what
     * the environment holds, so either adopt takes that value instead of overwriting it.
     */
    const rowActionFor = (op: PushOpRow) => {
        const target = adoptTargetOf(op, environment);
        return target ? fieldAdoption.rowActionFor(target) : undefined;
    };

    return (
        <>
            {!isMobile && (
                <ScrollArea p="md" style={{ borderRight: '1px solid var(--rgs-line)' }}>
                    <SectionHeading variant="group">Environments</SectionHeading>
                    {checklist}
                    {environment && (
                        <>
                            <SectionHeading variant="group">
                                Records in <span className="rgs-numeric">{environment}</span>
                                {view.entries.length > 0 && ` · ${view.entries.length}`}
                            </SectionHeading>
                            {view.entries.length > 0 ? (
                                <RecordNav view={view} />
                            ) : (
                                <Text fz="xs" c="dimmed" px="sm">
                                    {previewPending ? 'previewing…' : 'None'}
                                </Text>
                            )}
                        </>
                    )}
                </ScrollArea>
            )}
            <ScrollArea p={isMobile ? 'xs' : 'md'}>
                {isMobile && (
                    <Stack gap="xs" pb="sm">
                        <SectionHeading variant="group">Environments</SectionHeading>
                        {checklist}
                    </Stack>
                )}
                {isMobile && environment && view.entries.length > 0 && (
                    <Group gap={6} wrap="nowrap" pb="xs">
                        <Select
                            size="xs"
                            aria-label="Record"
                            placeholder="Record"
                            searchable
                            allowDeselect={false}
                            data={view.visible.map((entry) => ({ value: entry.id, label: recordName(entry).name }))}
                            value={view.active}
                            onChange={(value) => value && view.jump(value)}
                            style={{ flex: 3, minWidth: 0 }}
                        />
                        <Box style={{ flex: 2, minWidth: 0 }}>
                            <FieldFilter view={view} />
                        </Box>
                    </Group>
                )}
                {environment ? (
                    <PushRecords view={view} environment={environment} rowActionFor={rowActionFor} />
                ) : (
                    <ReviewEmpty>Tick an environment to review what a push writes there.</ReviewEmpty>
                )}
                {previewError ? (
                    <Alert color="red" mt="md" icon={<IconAlertTriangle size={16} stroke={1.7} />}>
                        {errorMessage(previewError)}
                    </Alert>
                ) : null}
            </ScrollArea>
        </>
    );
};

/**
 * `master → staging`. With two or more environments ticked it names each, and the one picked is
 * the one the record nav and pane are drawn against — the only place the review asks which.
 * Nothing until one is ticked: there is no direction to a push to nowhere.
 */
const EnvironmentPicker = ({
    tickedNames,
    environment,
    onPick,
}: {
    tickedNames: string[];
    environment: string | null;
    onPick: (name: string) => void;
}) => {
    if (environment === null) return null;
    if (tickedNames.length < 2) {
        return (
            <Text fz="xs" c="dimmed" className="rgs-numeric">
                master → {environment}
            </Text>
        );
    }
    return (
        <SegmentedControl
            size="xs"
            aria-label="Environment on screen"
            value={environment}
            onChange={onPick}
            data={tickedNames.map((name) => ({ value: name, label: <span className="rgs-numeric">{name}</span> }))}
        />
    );
};
